<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="calendar" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Leave Approvals</h3>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="clock" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Pending Approvals ({{ leaveRequests.length }})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search leave requests" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Leave Requests Table -->
          <div v-if="!loading && leaveRequests.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Employee</th>
                  <th scope="col" class="fw-semibold">Leave Type</th>
                  <th scope="col" class="fw-semibold">Start Date</th>
                  <th scope="col" class="fw-semibold">End Date</th>
                  <th scope="col" class="fw-semibold">Days</th>
                  <th scope="col" class="fw-semibold">Status</th>
                  <th scope="col" class="fw-semibold">Applied On</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="request in filteredLeaveRequests" :key="request.id">
                  <td>{{ request.employee?.first_name }} {{ request.employee?.last_name }}</td>
                  <td>{{ request.leave_type?.name || 'N/A' }}</td>
                  <td>{{ formatDate(request.start_date) }}</td>
                  <td>{{ formatDate(request.end_date) }}</td>
                  <td>{{ request.days }}</td>
                  <td>
                    <span :class="['badge', getStatusBadgeClass(request.status)]">
                      {{ capitalizeFirstLetter(request.status) }}
                    </span>
                  </td>
                  <td>{{ formatDate(request.created_at) }}</td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm btn-outline-primary me-2"
                      @click="viewLeaveRequest(request)"
                      title="View Details"
                    >
                      <vue-feather type="eye" size="14"></vue-feather>
                    </button>
                    <button
                      v-if="request.status === 'pending'"
                      class="btn btn-sm btn-outline-success me-2"
                      @click="openApproveModal(request)"
                      title="Approve"
                    >
                      <vue-feather type="check" size="14"></vue-feather>
                    </button>
                    <button
                      v-if="request.status === 'pending'"
                      class="btn btn-sm btn-outline-danger"
                      @click="openRejectModal(request)"
                      title="Reject"
                    >
                      <vue-feather type="x" size="14"></vue-feather>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && leaveRequests.length === 0" class="text-center py-5">
            <vue-feather type="check-circle" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Pending Approvals</h5>
            <p class="text-muted">You don't have any leave requests waiting for your approval.</p>
          </div>
        </Card>
      </div>
    </div>

    <!-- View Leave Request Modal -->
    <ViewLeaveRequestModal
      :show="showViewModal"
      :request="selectedRequest"
      @close="closeViewModal"
    />

    <!-- Approve Leave Request Modal -->
    <ApproveLeaveRequestModal
      :show="showApproveModal"
      :loading="saving"
      @close="closeApproveModal"
      @approve="handleApprove"
    />

    <!-- Reject Leave Request Modal -->
    <RejectLeaveRequestModal
      :show="showRejectModal"
      :loading="saving"
      @close="closeRejectModal"
      @reject="handleReject"
    />
  </div>
</template>

<script>
import formatDate from '@/utils/formatDate';
import leaveService from '@/api/leaveService';
import notify from '@/utils/notify';
import {decrypt} from "@/utils/crypto";
import ViewLeaveRequestModal from '@/components/leave/ViewLeaveRequestModal.vue';
import ApproveLeaveRequestModal from '@/components/leave/ApproveLeaveRequestModal.vue';
import RejectLeaveRequestModal from '@/components/leave/RejectLeaveRequestModal.vue';

export default {
  name: 'LeaveApprovals',
  components: {
    ViewLeaveRequestModal,
    ApproveLeaveRequestModal,
    RejectLeaveRequestModal
  },
  data() {
    return {
      loading: true,
      saving: false,
      profile: {},
      leaveRequests: [],
      selectedRequest: null,
      showViewModal: false,
      showApproveModal: false,
      showRejectModal: false,
      dataTableSearch: ''
    };
  },
  computed: {
    filteredLeaveRequests() {
      if (!this.dataTableSearch) return this.leaveRequests;

      const search = this.dataTableSearch.toLowerCase();
      return this.leaveRequests.filter(request => {
        return (
          (request.employee?.first_name?.toLowerCase().includes(search)) ||
          (request.employee?.last_name?.toLowerCase().includes(search)) ||
          (request.leave_type?.name?.toLowerCase().includes(search)) ||
          (request.status?.toLowerCase().includes(search))
        );
      });
    }
  },
  methods: {
    formatDate,

    async fetchLeaveRequests() {
      try {
        this.loading = true;
        this.leaveRequests = await leaveService.getLeaveApprovals(this.profile.id);
      } catch (error) {
        console.error('Error fetching leave approvals:', error);
        notify.alert.error('Failed to fetch leave approvals');
      } finally {
        this.loading = false;
      }
    },

    viewLeaveRequest(request) {
      this.selectedRequest = request;
      this.showViewModal = true;
    },

    openApproveModal(request) {
      this.selectedRequest = request;
      this.showApproveModal = true;
    },

    openRejectModal(request) {
      this.selectedRequest = request;
      this.showRejectModal = true;
    },

    closeViewModal() {
      this.showViewModal = false;
      this.selectedRequest = null;
    },

    closeApproveModal() {
      this.showApproveModal = false;
      this.selectedRequest = null;
    },

    closeRejectModal() {
      this.showRejectModal = false;
      this.selectedRequest = null;
    },

    async handleApprove(comments) {
      try {
        this.saving = true;
        await leaveService.approveLeaveRequest(this.selectedRequest.id, comments || '');
        const index = this.leaveRequests.findIndex(r => r.id === this.selectedRequest.id);
        if (index !== -1) {
          this.leaveRequests.splice(index, 1);
        }
        this.closeApproveModal();
        await this.fetchLeaveRequests();
      } catch (error) {
        console.error('Error approving leave request:', error);
        notify.alert.error(error.response?.data?.message || 'Failed to approve leave request');
      } finally {
        this.saving = false;
      }
    },

    async handleReject(reason) {
      try {
        this.saving = true;
        await leaveService.rejectLeaveRequest(this.selectedRequest.id, { rejection_reason: reason });
        const index = this.leaveRequests.findIndex(r => r.id === this.selectedRequest.id);
        if (index !== -1) {
          this.leaveRequests.splice(index, 1);
        }
        this.closeRejectModal();
        await this.fetchLeaveRequests();
      } catch (error) {
        console.error('Error rejecting leave request:', error);
        notify.alert.error(error.response?.data?.message || 'Failed to reject leave request');
      } finally {
        this.saving = false;
      }
    },

    capitalizeFirstLetter(string) {
      if (!string) return '';
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'approved':
          return 'bg-success';
        case 'rejected':
          return 'bg-danger';
        case 'cancelled':
          return 'bg-secondary';
        case 'pending':
        default:
          return 'bg-warning';
      }
    }
  },
  created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.fetchLeaveRequests();
  }
};
</script>
